import { React, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { GlobalStateContext } from '../utils/GlobalStateContext';
import { Shield } from 'lucide-react';
import '../style/Login.css';
import '../style/Mfa.css';

export default function MfaVerify() {
  const navigate = useNavigate();
  const { setCurrentUser } = useContext(GlobalStateContext);
  const BASE_URL = process.env.REACT_APP_API_URL;

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);

  const pending = JSON.parse(sessionStorage.getItem('mfaPending') || 'null');

  useEffect(() => {
    if (!pending?.pendingToken) navigate('/');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError('Enter the 6-digit code from your authenticator app.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BASE_URL}/mfa/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pendingToken: pending.pendingToken, code, rememberDevice }),
      });

      const contentType = response.headers.get('content-type');
      const rawText = await response.text();

      if (!response.ok || !contentType?.includes('application/json')) {
        const parsed = contentType?.includes('application/json') ? JSON.parse(rawText) : {};
        setError(parsed.error || 'Verification failed. Please try again.');
        return;
      }

      const responseData = JSON.parse(rawText);
      const roleData = responseData.data[0];
      const { first_name, last_name, type } = responseData;

      let roleKey = null;
      let route = null;

      switch (true) {
        case typeof roleData.rad_tech_id === 'number':
          roleKey = { rad_tech_id: roleData.rad_tech_id };
          route = '/radiology';
          break;
        case typeof roleData.lab_tech_id === 'number':
          roleKey = { lab_tech_id: roleData.lab_tech_id };
          route = '/labs';
          break;
        case typeof roleData.office_assistants_id === 'number':
          roleKey = { office_assistants_id: roleData.office_assistants_id };
          route = '/checkIn';
          break;
        case typeof roleData.clinicians_id === 'number':
          roleKey = { clinicians_id: roleData.clinicians_id };
          route = '/home';
          break;
        case typeof roleData.medical_assistants_id === 'number':
          roleKey = { medical_assistant_id: roleData.medical_assistants_id };
          route = '/home';
          break;
        case typeof roleData.biller_id === 'number':
          roleKey = { biller_id: roleData.biller_id };
          route = '/billing';
          break;
        case typeof roleData.admin_id === 'number':
          roleKey = { admin_id: roleData.admin_id };
          route = '/home';
          break;
        default:
          setError('Unexpected role. Please contact support.');
          return;
      }

      sessionStorage.removeItem('mfaPending');
      if (roleData.session_token) sessionStorage.setItem('sessionToken', roleData.session_token);
      setCurrentUser({ ...roleKey, first_name, last_name, type, mfa_enabled: true });
      navigate(route);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-bg-image" />
      <div className="login-card">
        <div className="login-header">
          <div className="mfa-shield-wrap">
            <Shield size={44} color="white" />
          </div>
          <h1 className="login-title">Two-Factor Authentication</h1>
          <p className="login-subtitle">
            {pending?.first_name ? `Welcome, ${pending.first_name}. ` : ''}
            Enter the 6-digit code from your authenticator app.
          </p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <div className="mfa-code-wrap">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000 000"
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
              className="mfa-code-input"
              autoFocus
            />
          </div>

          <label className="mfa-remember-label">
            <input
              type="checkbox"
              checked={rememberDevice}
              onChange={e => setRememberDevice(e.target.checked)}
              className="mfa-remember-checkbox"
            />
            Remember this device for 30 days
          </label>

          {error && <p className="mfa-error">{error}</p>}

          <button type="submit" className="login-submit-btn" disabled={loading || code.length !== 6}>
            <Shield size={18} />
            <span>{loading ? 'Verifying…' : 'Verify'}</span>
          </button>

          <button
            type="button"
            className="mfa-back-btn"
            onClick={() => { sessionStorage.removeItem('mfaPending'); navigate('/'); }}
          >
            Back to Sign In
          </button>
        </form>

        <div className="login-footer">
          <span>Powered by Health Direct Plus</span>
        </div>
      </div>
    </div>
  );
}
